import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { fetchConstructionList, uploadConstructionList } from '../api/postApi';
import Pagination from '../components/Pagination';

const LIST_COLUMN_CONFIG = [
  { key: 'installationYear', label: '설치연도' },
  { key: 'businessName', label: '사업명' },
  { key: 'installationCompany', label: '시공 업체' },
  { key: 'possessor', label: '설비 소유자' },
  { key: 'address', label: '주소' },
];

const REQUIRED_KEYS = [
  'installationYear',
  'businessName',
  'managementNumber',
  'possessor',
  'possessorPhoneNumber',
  'address',
  'detailAddress',
  'installationCompany',
  'contractorManager',
  'contractorPhoneNumber',
  'moduleManufacturer',
  'moduleModelName',
  'moduleQuantity',
  'inverterManufacturer',
  'inverterModelName',
  'ratedVoltage',
  'capacity',
  'inverterQuantity',
  'inverterCapacity',
];

const HEADER_KEY_ALIASES = {
  설치연도: 'installationYear',
  설치년도: 'installationYear',
  사업명: 'businessName',
  관리번호: 'managementNumber',
  관리번호1: 'managementNumber',
  점검번호: 'managementNumber',
  설비소유자: 'possessor',
  설비소유자성명: 'possessor',
  소유자: 'possessor',
  설비소유자전화번호: 'possessorPhoneNumber',
  소유자전화번호: 'possessorPhoneNumber',
  주소: 'address',
  상세주소: 'detailAddress',
  시공업체: 'installationCompany',
  시공회사: 'installationCompany',
  시공업체담당자: 'contractorManager',
  담당자: 'contractorManager',
  시공업체담당자전화번호: 'contractorPhoneNumber',
  담당자전화번호: 'contractorPhoneNumber',
  모듈제조사: 'moduleManufacturer',
  모듈모델명: 'moduleModelName',
  모듈수량: 'moduleQuantity',
  인버터제조사: 'inverterManufacturer',
  인버터모델명: 'inverterModelName',
  정격전압: 'ratedVoltage',
  용량: 'capacity',
  설비용량: 'capacity',
  설비용량kw: 'capacity',
  인버터용량: 'inverterCapacity',
  인버터용량kw: 'inverterCapacity',
  인버터수량: 'inverterQuantity',
  접속반유무: 'junctionBoxYn',
  접속반: 'junctionBoxYn',
  참고사항: 'note',
  비고: 'note',
  특이사항: 'note',
};

const normalizeHeader = (header) => String(header ?? '').replace(/\s+/g, '').trim();

const toConstructionPayloadRow = (row) => {
  const mapped = {};
  Object.entries(row ?? {}).forEach(([header, value]) => {
    const normalized = normalizeHeader(header);
    const targetKey = HEADER_KEY_ALIASES[normalized];
    if (!targetKey) return;
    mapped[targetKey] = value === null || value === undefined ? '' : String(value).trim();
  });
  return coerceConstructionNumericFields(mapped);
};

const parseExcelInt = (raw) => {
  const s = String(raw ?? '').replace(/,/g, '').trim();
  if (s === '') return null;
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : null;
};

const parseExcelDouble = (raw) => {
  const s = String(raw ?? '').replace(/,/g, '').trim();
  if (s === '') return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
};

/** 엑셀은 문자열로 읽히므로 API(ConstructionDto)에 맞게 숫자 필드만 변환 */
const coerceConstructionNumericFields = (row) => ({
  ...row,
  moduleQuantity: parseExcelInt(row.moduleQuantity),
  inverterQuantity: parseExcelInt(row.inverterQuantity),
  ratedVoltage: parseExcelInt(row.ratedVoltage),
  capacity: parseExcelDouble(row.capacity),
  inverterCapacity: parseExcelDouble(row.inverterCapacity),
});

const hasAnyValue = (row) =>
  Object.values(row).some((value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'number') return Number.isFinite(value);
    return String(value).trim() !== '';
  });

const toDisplayValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

function PostInstallationPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [data, setData] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [isUploadPopupOpen, setIsUploadPopupOpen] = useState(false);
  const [pendingFileName, setPendingFileName] = useState('');
  const [pendingPayload, setPendingPayload] = useState([]);
  const [filters, setFilters] = useState({
    possessor: '',
    installationCompany: '',
    businessName: '',
    address: '',
    startYear: '',
    endYear: '',
  });
  const [page, setPage] = useState(0);
  const size = 10;

  const loadRows = useCallback(
    async (override = {}) => {
      const query = {
        ...filters,
        page,
        size,
        sort: 'id,desc',
        ...override,
      };
      const result = await fetchConstructionList(query);
      setData(result);
      setRows(Array.isArray(result?.content) ? result.content : []);
    },
    [filters, page, size]
  );

  useEffect(() => {
    const run = async () => {
      try {
        await loadRows();
      } catch (error) {
        window.alert(`시공목록 조회 실패: ${error.message}`);
      }
    };
    run();
  }, [loadRows]);

  const parseExcelToPayload = async (file) => {
    if (!file) return;

    setLoading(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) {
        throw new Error('시트가 없는 파일입니다.');
      }

      const firstSheet = workbook.Sheets[firstSheetName];
      const rawRows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' });
      const rawHeaders = Array.isArray(rawRows) && rawRows.length > 0 ? rawRows[0] : [];
      const detectedKeys = new Set(
        rawHeaders
          .map((header) => HEADER_KEY_ALIASES[normalizeHeader(header)])
          .filter(Boolean)
      );
      const missingKeys = REQUIRED_KEYS.filter((key) => !detectedKeys.has(key));
      if (missingKeys.length > 0) {
        throw new Error(`필수 컬럼이 누락되었습니다: ${missingKeys.join(', ')}`);
      }

      const jsonRows = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
      if (!Array.isArray(jsonRows) || jsonRows.length === 0) {
        throw new Error('엑셀 데이터가 비어 있습니다.');
      }

      const payload = jsonRows
        .map(toConstructionPayloadRow)
        .filter((row) => Object.keys(row).length > 0)
        .filter(hasAnyValue);
      if (payload.length === 0) {
        throw new Error('저장 가능한 데이터 행이 없습니다.');
      }

      setPendingFileName(file.name);
      setPendingPayload(payload);
    } catch (error) {
      window.alert(`엑셀 처리 실패: ${error.message}`);
      setPendingFileName('');
      setPendingPayload([]);
    } finally {
      setLoading(false);
    }
  };

  const onClickUpload = () => {
    setPendingFileName('');
    setPendingPayload([]);
    setIsUploadPopupOpen(true);
  };

  const onCloseUploadPopup = () => {
    if (loading) return;
    setIsUploadPopupOpen(false);
    setDragging(false);
    setPendingFileName('');
    setPendingPayload([]);
  };

  const onConfirmUpload = async () => {
    if (pendingPayload.length === 0 || loading) return;
    const shouldUpload = window.confirm('저장하시겠습니까?');
    if (!shouldUpload) return;

    setLoading(true);
    try {
      const savedCount = await uploadConstructionList(pendingPayload);
      setPage(0);
      await loadRows({ page: 0 });
      setUploadedFileName(pendingFileName);
      setIsUploadPopupOpen(false);
      setPendingFileName('');
      setPendingPayload([]);
      window.alert(`엑셀 업로드 완료: ${savedCount}건 저장되었습니다.`);
    } catch (error) {
      window.alert(`엑셀 업로드 실패: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const onFileChange = async (event) => {
    const file = event.target.files?.[0];
    await parseExcelToPayload(file);
    event.target.value = '';
  };

  const onDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragging(true);
  };

  const onDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragging(false);
  };

  const onDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    await parseExcelToPayload(file);
  };

  const onChangeFilter = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onSearch = async (event) => {
    event.preventDefault();
    setPage(0);
    try {
      await loadRows({ page: 0 });
    } catch (error) {
      window.alert(`시공목록 조회 실패: ${error.message}`);
    }
  };

  const onMovePage = (nextPage) => {
    setPage(nextPage);
  };

  return (
    <section className="page">
      <div className="page-header">
        <h2>시공정보 목록 조회</h2>
        <div className="button-row">
          <button type="button" className="secondary" onClick={() => navigate('/posts/installation/new')}>
            시공정보 작성
          </button>
          <a href={`${process.env.PUBLIC_URL}/시공 목록 업로드.xlsx`} download>
            <button type="button" className="secondary">시공 정보 업로드 양식 다운로드</button>
          </a>
          <button type="button" onClick={onClickUpload} disabled={loading}>
            엑셀 업로드
          </button>
        </div>
      </div>

      <p className="upload-status">
        {loading
          ? '엑셀 파일을 처리 중입니다...'
          : uploadedFileName
            ? `최근 업로드 파일: ${uploadedFileName}`
            : `총 시공 목록 건수: ${data?.totalElements ?? 0} 건`}
      </p>

      <form className="filter-row" onSubmit={onSearch}>
        <input name="businessName" placeholder="사업명" value={filters.businessName} onChange={onChangeFilter} />
        <input name="address" placeholder="주소" value={filters.address} onChange={onChangeFilter} />
        <input name="possessor" placeholder="소유자" value={filters.possessor} onChange={onChangeFilter} />
        <input
          name="installationCompany"
          placeholder="시공사"
          value={filters.installationCompany}
          onChange={onChangeFilter}
        />
        <input
          name="startYear"
          type="number"
          min="1900"
          max="2100"
          placeholder="시작 연도"
          value={filters.startYear}
          onChange={onChangeFilter}
        />
        <input
          name="endYear"
          type="number"
          min="1900"
          max="2100"
          placeholder="종료 연도"
          value={filters.endYear}
          onChange={onChangeFilter}
        />
        <button type="submit">검색</button>
      </form>

      <div className="table-wrapper fixed-list-wrapper">
        <table>
          <thead>
            <tr>
              <th>순번</th>
              {LIST_COLUMN_CONFIG.map((column) => (
                <th key={column.key}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={LIST_COLUMN_CONFIG.length + 1}>저장된 시공 목록이 없습니다.</td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr
                  key={row.id ?? `construction-row-${index}`}
                  onClick={() => navigate(`/posts/installation/${row.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{toDisplayValue(row.id)}</td>
                  {LIST_COLUMN_CONFIG.map((column) => (
                    <td key={`${index}-${column.key}`}>{toDisplayValue(row[column.key])}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        totalPages={data?.totalPages ?? 1}
        onPageChange={onMovePage}
      />

      {isUploadPopupOpen && (
        <div className="modal-backdrop" onClick={onCloseUploadPopup}>
          <div className="upload-modal" onClick={(event) => event.stopPropagation()}>
            <h3>엑셀 업로드</h3>

            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={onFileChange}
              className="hidden-file-input"
            />

            <div
              className={`excel-dropzone ${dragging ? 'dragging' : ''}`}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            >
              <p>엑셀 파일을 드래그해서 놓거나, 아래 버튼으로 파일을 선택하세요.</p>
              <p className="dropzone-subtext">지원 형식: .xlsx, .xls</p>
            </div>

            <div className="button-row modal-actions">
              <button type="button" className="secondary" onClick={() => fileInputRef.current?.click()} disabled={loading}>
                파일 선택
              </button>
              <button type="button" onClick={onConfirmUpload} disabled={loading || pendingPayload.length === 0}>
                확인
              </button>
              <button type="button" className="danger" onClick={onCloseUploadPopup} disabled={loading}>
                취소
              </button>
            </div>

            <p className="upload-status">
              {loading
                ? '엑셀 파일을 처리 중입니다...'
                : pendingFileName
                  ? `${pendingFileName} (${pendingPayload.length}건) - 저장하시겠습니까?`
                  : '업로드할 엑셀 파일을 선택해주세요.'}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

export default PostInstallationPage;
